import { useEffect, useState, useCallback } from 'react';
import { getMediaCaptureService } from '../container';
import type { CaptureSessionSnapshot } from '../ports';
import type { CaptureSettings } from '@shared/types';

/**
 * Thin React adapter over MediaCaptureService. Components should use this
 * hook (or the mediaCaptureStore, for state needed outside the React tree)
 * rather than importing the service/container directly — this keeps the
 * dependency direction one-way: UI -> hook -> service interface.
 */
export function useMediaCapture() {
  const service = getMediaCaptureService();
  const [snapshot, setSnapshot] = useState<CaptureSessionSnapshot>(service.getSnapshot());

  useEffect(() => {
    return service.subscribe(setSnapshot);
  }, [service]);

  const start = useCallback((settings: CaptureSettings) => service.start(settings), [service]);
  const stop = useCallback(() => service.stop(), [service]);
  const getOutputStream = useCallback(() => service.getOutputStream(), [service]);
  const updateSettings = useCallback(
    (partial: Partial<CaptureSettings>) => service.updateSettings(partial),
    [service],
  );

  return {
    state: snapshot.state,
    settings: snapshot.settings,
    error: snapshot.error,
    stream: service.getOutputStream(),
    getOutputStream,
    start,
    stop,
    updateSettings,
  };
}
